/**
 * โรงงานและคลัง/ที่เก็บ — ดู สร้าง และเปลี่ยนชื่อ (D-176)
 *
 * รหัสโรงงาน/คลังแก้ไม่ได้หลังสร้าง (ถูกอ้างทั่วระบบ) เปลี่ยนได้เฉพาะชื่อ และไม่มีลบ/ปิดใช้งาน
 * ทุกตารางมาจากเซิร์ฟเวอร์เสมอและเปลี่ยนได้ทางเดียวคือโหลดใหม่หลังสำเร็จ — ไม่มี optimistic update
 *
 * ปุ่มเขียนถูกกันที่นี่ด้วย `RBAC_ADMIN` CREATE/EDIT — เป็น UX เท่านั้น เซิร์ฟเวอร์ตัดสินเสมอ
 */

import { useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { ruleFromFailure } from '../../lib/rbacAdmin/client';
import {
  createLocation,
  createPlant,
  fetchCompanyCodes,
  fetchLocations,
  fetchPlants,
  renameLocation,
  renamePlant,
  type OrgAdminFailure,
} from '../../lib/orgAdmin/client';
import {
  CREATABLE_LOCATION_TYPES,
  LOCATION_TYPE_LABELS,
  ORG_RULES,
  type CompanyCode,
  type Plant,
  type StorageLocation,
} from '../../lib/orgAdmin/types';

const ORG_ADMIN_MODULE = 'RBAC_ADMIN';
const PLANT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]*$/;
const LOCATION_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]*$/;

export interface PlantsScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type LoadState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | OrgAdminFailure;

interface Copy {
  readonly heading: string;
  readonly text: string;
}

function ruleCopy(rule: string | null): Copy | null {
  switch (rule) {
    case ORG_RULES.PLANT_CODE_TAKEN:
      return { heading: 'รหัสโรงงานนี้มีอยู่แล้ว', text: 'เลือกรหัสอื่น ไม่มีการบันทึกใด ๆ' };
    case ORG_RULES.PLANT_NOT_FOUND:
      return { heading: 'ไม่พบโรงงานนี้', text: 'โหลดรายการใหม่แล้วเลือกอีกครั้ง' };
    case ORG_RULES.COMPANY_CODE_NOT_FOUND:
      return { heading: 'ไม่พบ Company Code นี้', text: 'เลือก Company Code จากรายการที่มี' };
    case ORG_RULES.STORAGE_LOCATION_CODE_TAKEN:
      return {
        heading: 'รหัสคลัง/ที่เก็บนี้มีอยู่แล้วในโรงงานนี้',
        text: 'เลือกรหัสอื่น ไม่มีการบันทึกใด ๆ',
      };
    case ORG_RULES.STORAGE_LOCATION_NOT_FOUND:
      return { heading: 'ไม่พบคลัง/ที่เก็บนี้', text: 'โหลดรายการใหม่แล้วเลือกอีกครั้ง' };
    case ORG_RULES.SUBCONTRACTOR_LOCATION_NOT_CREATABLE:
      return {
        heading: 'สร้างที่เก็บผู้รับจ้างช่วงจากหน้านี้ไม่ได้',
        text: 'ที่เก็บชนิดนี้ต้องผูกผู้ขาย จึงตั้งพร้อมการตั้งค่า outsource',
      };
    case ORG_RULES.LOCATION_TYPE_INVALID:
      return { heading: 'ชนิดที่เก็บไม่ถูกต้อง', text: 'เลือกจากรายการชนิดที่มีให้' };
    default:
      return null;
  }
}

function genericCopy(failure: OrgAdminFailure): Copy {
  switch (failure.status) {
    case 'unauthenticated':
      return { heading: 'ต้องเข้าสู่ระบบก่อน', text: 'เซสชันหมดอายุหรือถูกยกเลิก (401) เข้าสู่ระบบใหม่' };
    case 'forbidden':
      return {
        heading: 'บัญชีนี้ไม่ใช่ผู้ดูแลระบบ',
        text: 'เซิร์ฟเวอร์ตอบ 403 — หน้าจอนี้ต้องใช้สิทธิ์ RBAC_ADMIN ซึ่งมีเฉพาะบทบาท ADMIN',
      };
    case 'not-found':
      return { heading: 'ไม่พบสิ่งที่อ้างถึง', text: 'เซิร์ฟเวอร์ตอบ 404 โหลดรายการใหม่แล้วลองอีกครั้ง' };
    case 'conflict':
      return { heading: 'ข้อมูลชนกับที่มีอยู่', text: 'เซิร์ฟเวอร์ตอบ 409 โหลดรายการใหม่แล้วลองอีกครั้ง' };
    case 'rule-violation':
      return { heading: 'คำขอติดกฎทางธุรกิจ', text: 'เซิร์ฟเวอร์ตอบ 422 แก้เงื่อนไขที่ระบุแล้วลองอีกครั้ง' };
    case 'bad-request':
      return {
        heading: 'คำขอไม่ถูกต้อง',
        text: 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลไม่ตรงรูปแบบ (เช่น รหัสผิดรูป) ตรวจแล้วลองอีกครั้ง',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: 'คำขอไปถึงเซิร์ฟเวอร์แต่ทำงานไม่สำเร็จ โหลดข้อมูลใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: `${failure.detail} — โหลดข้อมูลใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านไม่ครบ ไม่เดาค่าแทน',
      };
    case 'cancelled':
      return { heading: 'ยกเลิกการเรียกข้อมูลแล้ว', text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ' };
  }
}

function FailureNotice({
  failure,
  onRetry,
}: {
  readonly failure: OrgAdminFailure;
  readonly onRetry?: () => void;
}) {
  const rule = ruleFromFailure(failure);
  const copy = ruleCopy(rule) ?? genericCopy(failure);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';
  return (
    <div
      className="cap-notice"
      data-testid="plants-failure"
      data-failure={failure.status}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code">{rule}</code>
        </p>
      )}
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

interface Editing {
  readonly kind: 'plant' | 'location';
  readonly code: string;
  readonly value: string;
}

export function PlantsScreen({ permissions, apiBaseUrl, fetchImpl }: PlantsScreenProps) {
  const canCreate = can(permissions, ORG_ADMIN_MODULE, 'CREATE');
  const canEdit = can(permissions, ORG_ADMIN_MODULE, 'EDIT');
  const options = { apiBaseUrl, fetchImpl };

  const [plants, setPlants] = useState<LoadState<Plant>>({ status: 'loading' });
  const [companies, setCompanies] = useState<LoadState<CompanyCode>>({ status: 'loading' });
  const [locations, setLocations] = useState<LoadState<StorageLocation>>({ status: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<OrgAdminFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);

  const [plantCode, setPlantCode] = useState('');
  const [plantName, setPlantName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState<string>(CREATABLE_LOCATION_TYPES[0]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const request = { apiBaseUrl, fetchImpl, signal: controller.signal };
    void fetchPlants(request).then((result) => {
      if (active && result.status !== 'cancelled') {
        setPlants(result);
      }
    });
    void fetchCompanyCodes(request).then((result) => {
      if (active && result.status !== 'cancelled') {
        setCompanies(result);
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  useEffect(() => {
    if (selected === null) {
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    void fetchLocations(selected, { apiBaseUrl, fetchImpl, signal: controller.signal }).then(
      (result) => {
        if (active && result.status !== 'cancelled') {
          setLocations(result);
        }
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, selected, reloadToken]);

  const reload = () => {
    setReloadToken((token) => token + 1);
  };

  const plantList = plants.status === 'ready' ? plants.items : [];
  const companyList = companies.status === 'ready' ? companies.items : [];
  const locationList = locations.status === 'ready' ? locations.items : [];
  const plantCodeValid = PLANT_CODE_PATTERN.test(plantCode) && plantCode.length <= 10;
  const locationCodeValid =
    LOCATION_CODE_PATTERN.test(locationCode) && locationCode.length <= 20;

  const finish = (result: { readonly status: string }, message: string): boolean => {
    setBusy(false);
    if (result.status === 'cancelled') {
      return false;
    }
    if (result.status !== 'ready') {
      setFailure(result as OrgAdminFailure);
      return false;
    }
    setNotice(message);
    reload();
    return true;
  };

  const begin = () => {
    setBusy(true);
    setFailure(null);
    setNotice(null);
  };

  const submitPlant = async () => {
    if (!canCreate || busy || !plantCodeValid || plantName.trim() === '' || companyCode === '') {
      return;
    }
    begin();
    const result = await createPlant(
      { plant_code: plantCode, name: plantName.trim(), company_code: companyCode },
      options,
    );
    if (finish(result, `สร้างโรงงาน ${plantCode} แล้ว — ยังไม่มีใครเห็นโรงงานนี้ จนกว่าจะให้สิทธิ์เข้าโรงงานในหน้าพนักงาน`)) {
      setPlantCode('');
      setPlantName('');
    }
  };

  const submitLocation = async () => {
    if (
      !canCreate ||
      busy ||
      selected === null ||
      !locationCodeValid ||
      locationName.trim() === ''
    ) {
      return;
    }
    begin();
    const result = await createLocation(
      selected,
      {
        storage_location_code: locationCode,
        name: locationName.trim(),
        location_type: locationType,
      },
      options,
    );
    if (finish(result, `สร้างคลัง/ที่เก็บ ${locationCode} ในโรงงาน ${selected} แล้ว`)) {
      setLocationCode('');
      setLocationName('');
    }
  };

  const submitRename = async () => {
    if (!canEdit || busy || editing === null || editing.value.trim() === '') {
      return;
    }
    const target = editing;
    begin();
    const result =
      target.kind === 'plant'
        ? await renamePlant(target.code, target.value.trim(), options)
        : selected === null
          ? ({ status: 'cancelled' } as const)
          : await renameLocation(selected, target.code, target.value.trim(), options);
    if (finish(result, `เปลี่ยนชื่อ ${target.code} แล้ว`)) {
      setEditing(null);
    }
  };

  const renameCell = (kind: 'plant' | 'location', code: string, name: string) => {
    if (editing !== null && editing.kind === kind && editing.code === code) {
      return (
        <form
          className="cap-filters"
          data-testid={`rename-form-${kind}-${code}`}
          onSubmit={(event) => {
            event.preventDefault();
            void submitRename();
          }}
        >
          <input
            className="cap-input"
            aria-label={`ชื่อใหม่ของ ${code}`}
            data-testid={`rename-input-${kind}-${code}`}
            maxLength={120}
            value={editing.value}
            onChange={(event) => {
              setEditing({ kind, code, value: event.target.value });
            }}
          />
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid={`rename-save-${kind}-${code}`}
            disabled={busy || editing.value.trim() === ''}
          >
            บันทึก
          </button>
          <button
            type="button"
            className="cap-button cap-button--ghost"
            disabled={busy}
            onClick={() => {
              setEditing(null);
            }}
          >
            ยกเลิก
          </button>
        </form>
      );
    }
    return (
      <>
        {name}{' '}
        {canEdit ? (
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid={`rename-${kind}-${code}`}
            disabled={busy}
            onClick={() => {
              setEditing({ kind, code, value: name });
            }}
          >
            เปลี่ยนชื่อ
          </button>
        ) : null}
      </>
    );
  };

  return (
    <div className="cap-page" data-testid="plants-screen">
      <div className="cap-page__head">
        <h1 className="cap-page__title">
          โรงงานและคลัง/ที่เก็บ{' '}
          <InfoHint
            label="เกี่ยวกับรหัสโรงงาน"
            text="รหัสโรงงานและรหัสคลังแก้ไม่ได้หลังสร้างเพราะถูกอ้างทั่วระบบ เปลี่ยนได้เฉพาะชื่อ และยังไม่มีการลบหรือปิดใช้งาน โรงงานใหม่ยังไม่มีใครเห็นจนกว่าจะให้สิทธิ์เข้าโรงงานในหน้าพนักงาน"
          />
        </h1>
      </div>

      {!canEdit && !canCreate ? (
        <div className="cap-notice" data-testid="plants-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <p className="cap-notice__text">
            สิทธิ์ชุดนี้ไม่มี CREATE/EDIT ของโมดูล {ORG_ADMIN_MODULE} ฟอร์มและปุ่มแก้ไขจึงไม่แสดง
            (เซิร์ฟเวอร์เป็นผู้ตัดสินจริงทุกคำขอ)
          </p>
        </div>
      ) : null}

      {notice === null ? null : (
        <div className="cap-notice" data-testid="plants-notice" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <p className="cap-notice__text">{notice}</p>
        </div>
      )}
      {failure === null ? null : <FailureNotice failure={failure} onRetry={reload} />}

      {canCreate ? (
        <form
          className="cap-filters"
          data-testid="plant-create-form"
          aria-label="สร้างโรงงาน"
          onSubmit={(event) => {
            event.preventDefault();
            void submitPlant();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              data-testid="plant-create-code"
              autoComplete="off"
              maxLength={10}
              value={plantCode}
              onChange={(event) => {
                setPlantCode(event.target.value.toUpperCase());
              }}
            />
            {plantCode !== '' && !plantCodeValid ? (
              <span className="cap-field__hint cap-field__hint--error">
                ใช้ A-Z 0-9 และ - เท่านั้น ขึ้นต้นด้วยตัวอักษรหรือตัวเลข
              </span>
            ) : null}
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ชื่อโรงงาน</span>
            <input
              className="cap-input"
              data-testid="plant-create-name"
              autoComplete="off"
              maxLength={120}
              value={plantName}
              onChange={(event) => {
                setPlantName(event.target.value);
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">Company Code</span>
            <select
              className="cap-input"
              data-testid="plant-create-company"
              value={companyCode}
              onChange={(event) => {
                setCompanyCode(event.target.value);
              }}
            >
              <option value="">— เลือก —</option>
              {companyList.map((company) => (
                <option key={company.company_code} value={company.company_code}>
                  {company.company_code} — {company.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="plant-create-submit"
            disabled={busy || !plantCodeValid || plantName.trim() === '' || companyCode === ''}
          >
            {busy ? 'กำลังบันทึก…' : 'สร้างโรงงาน'}
          </button>
        </form>
      ) : null}

      {plants.status === 'loading' ? (
        <div className="cap-notice" data-testid="plants-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการโรงงาน…</p>
        </div>
      ) : null}
      {plants.status !== 'loading' && plants.status !== 'ready' ? (
        <FailureNotice failure={plants} onRetry={reload} />
      ) : null}
      {companies.status !== 'loading' && companies.status !== 'ready' ? (
        <FailureNotice failure={companies} onRetry={reload} />
      ) : null}

      {plants.status === 'ready' && plantList.length === 0 ? (
        <div className="cap-notice" data-testid="plants-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีโรงงาน</StatusBadge>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {plants.status === 'ready' && plantList.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="โรงงาน">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="plants-table">
              <thead>
                <tr>
                  <th scope="col">รหัสโรงงาน</th>
                  <th scope="col">ชื่อ</th>
                  <th scope="col">Company Code</th>
                  <th scope="col">จำนวนคลัง/ที่เก็บ</th>
                  <th scope="col">คลัง/ที่เก็บ</th>
                </tr>
              </thead>
              <tbody>
                {plantList.map((plant) => (
                  <tr key={plant.plant_code} data-testid={`plant-row-${plant.plant_code}`}>
                    <th scope="row" className="cap-table__row-head">
                      <code className="cap-table__code">{plant.plant_code}</code>
                    </th>
                    <td>{renameCell('plant', plant.plant_code, plant.name)}</td>
                    <td>{plant.company_code}</td>
                    <td>{plant.storage_location_count}</td>
                    <td>
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`plant-select-${plant.plant_code}`}
                        aria-pressed={selected === plant.plant_code}
                        onClick={() => {
                          setSelected(plant.plant_code);
                          setLocations({ status: 'loading' });
                          setEditing(null);
                        }}
                      >
                        {selected === plant.plant_code ? 'กำลังดู' : 'ดู/จัดการ'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {selected !== null ? (
        <section className="cap-card" aria-label={`คลัง/ที่เก็บของโรงงาน ${selected}`}>
          <div className="cap-card__head">
            <h2 className="cap-card__title" data-testid="locations-heading">
              คลัง/ที่เก็บของโรงงาน {selected}
            </h2>
          </div>

          {canCreate ? (
            <form
              className="cap-filters"
              data-testid="location-create-form"
              aria-label="สร้างคลัง/ที่เก็บ"
              onSubmit={(event) => {
                event.preventDefault();
                void submitLocation();
              }}
            >
              <label className="cap-field">
                <span className="cap-field__label">รหัสคลัง/ที่เก็บ</span>
                <input
                  className="cap-input"
                  data-testid="location-create-code"
                  autoComplete="off"
                  maxLength={20}
                  value={locationCode}
                  onChange={(event) => {
                    setLocationCode(event.target.value.toUpperCase());
                  }}
                />
                {locationCode !== '' && !locationCodeValid ? (
                  <span className="cap-field__hint cap-field__hint--error">
                    ใช้ A-Z 0-9 _ และ - เท่านั้น
                  </span>
                ) : null}
              </label>
              <label className="cap-field">
                <span className="cap-field__label">ชื่อ</span>
                <input
                  className="cap-input"
                  data-testid="location-create-name"
                  autoComplete="off"
                  maxLength={120}
                  value={locationName}
                  onChange={(event) => {
                    setLocationName(event.target.value);
                  }}
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">ชนิด</span>
                <select
                  className="cap-input"
                  data-testid="location-create-type"
                  value={locationType}
                  onChange={(event) => {
                    setLocationType(event.target.value);
                  }}
                >
                  {CREATABLE_LOCATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type} — {LOCATION_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="location-create-submit"
                disabled={busy || !locationCodeValid || locationName.trim() === ''}
              >
                {busy ? 'กำลังบันทึก…' : 'สร้างคลัง/ที่เก็บ'}
              </button>
            </form>
          ) : null}

          <p className="cap-card__body cap-field__hint" data-testid="locations-code-hint">
            รหัสคลัง/ที่เก็บใช้ชุดเดียวกับ Store Management เช่น A4-1, PCMAT, RECEIVING
            (วัตถุดิบ/รับของ) และ {selected}-FG001, {selected}-WIP001, {selected}-QC001
            (งานผลิต) — ตั้งให้ตรงกัน ไม่งั้น Store กับ ERP จะคุยกันไม่รู้เรื่อง
          </p>

          {locations.status === 'loading' ? (
            <p className="cap-card__body cap-notice__text" data-testid="locations-loading">
              กำลังโหลดคลัง/ที่เก็บ…
            </p>
          ) : null}
          {locations.status !== 'loading' && locations.status !== 'ready' ? (
            <div className="cap-card__body">
              <FailureNotice failure={locations} onRetry={reload} />
            </div>
          ) : null}
          {locations.status === 'ready' && locationList.length === 0 ? (
            <p className="cap-card__body cap-field__hint" data-testid="locations-empty">
              โรงงานนี้ยังไม่มีคลัง/ที่เก็บ
            </p>
          ) : null}
          {locations.status === 'ready' && locationList.length > 0 ? (
            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="locations-table">
                <thead>
                  <tr>
                    <th scope="col">รหัส</th>
                    <th scope="col">ชื่อ</th>
                    <th scope="col">ชนิด</th>
                  </tr>
                </thead>
                <tbody>
                  {locationList.map((location) => (
                    <tr
                      key={location.storage_location_code}
                      data-testid={`location-row-${location.storage_location_code}`}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{location.storage_location_code}</code>
                      </th>
                      <td>
                        {renameCell('location', location.storage_location_code, location.name)}
                      </td>
                      <td>
                        {location.location_type}
                        {LOCATION_TYPE_LABELS[location.location_type] === undefined
                          ? ''
                          : ` — ${LOCATION_TYPE_LABELS[location.location_type]}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
